import { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import {
  BaseResponse,
  ClientConfig,
  ItecAxiosRequestConfig,
  ItecParams,
  HasError
} from "../../types";
import { ErrorName, ItecError } from "../../errors";

export class BaseClient {
  protected axiosInstance: AxiosInstance;
  protected clientConfig: ClientConfig;

  constructor(axiosInstance: AxiosInstance, clientConfig?: ClientConfig) {
    this.axiosInstance = axiosInstance;
    this.clientConfig = clientConfig || {};
  }

  private handleResponse<TResponse>(
    response: AxiosResponse<BaseResponse<TResponse>>
  ) {
    if ((<HasError>response.data).tieneError) {
      throw new ItecError({
        name: "SERVER_ERROR",
        message: (<HasError>response.data).descripcionError,
      });
    }

    return response.data as TResponse;
  }

  private handleError(error: any, config?: { log?: boolean }) {
    let itecError: ItecError;

    if (error instanceof AxiosError) {
      if (!error.response) {
        itecError = new ItecError({
          name: "NETWORK_ERROR",
          message: error.message,
          cause: error.cause,
        });
      } else if (error.response.status === 401) {
        itecError = new ItecError({
          name: "UNAUTHORIZED",
          message: error.response.statusText,
          cause: error.cause,
        });
      } else if (error.response.status === 404) {
        itecError = new ItecError({
          name: "NOT_FOUND",
          message: error.response.statusText,
          cause: error.cause,
        });
      } else {
        itecError = new ItecError({
          name: "NETWORK_ERROR",
          message: error.message,
          cause: error.cause,
        });
      }
    } else if (error instanceof ItecError) {
      itecError = error;
    } else {
      itecError = new ItecError({
        name: "UNKOWN_ERROR",
        message: "",
        cause: error,
      });
    }

    const callback = this.clientConfig.errors?.callbacks?.[itecError.name];

    if (callback) {
      callback(itecError);
    }

    return itecError;
  }

  protected async get<TResponse, TParams>(
    url: string,
    params: ItecParams<TParams>,
    config?: ItecAxiosRequestConfig
  ): Promise<TResponse> {
    try {
      const response = await this.axiosInstance.get<BaseResponse<TResponse>>(
        url,
        {
          ...config,
          params: {
            ...params.params,
            ...params.pagination,
          },
        }
      );

      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  protected async post<TData, TParams, TResponse>(
    url: string,
    data: TData,
    params: ItecParams<TParams>,
    config?: ItecAxiosRequestConfig
  ): Promise<TResponse> {
    try {
      const response = await this.axiosInstance.post<BaseResponse<TResponse>>(
        url,
        data,
        {
          ...config,
          params: {
            ...params.params,
            ...params.pagination,
          },
        }
      );

      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  protected async put<TData, TParams, TResponse>(
    url: string,
    data: TData,
    params: ItecParams<TParams>,
    config?: ItecAxiosRequestConfig
  ): Promise<TResponse> {
    try {
      const response = await this.axiosInstance.put<BaseResponse<TResponse>>(
        url,
        data,
        {
          ...config,
          params: {
            ...params.params,
            ...params.pagination,
          },
        }
      );

      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  protected async delete<TResponse, TParams>(
    url: string,
    params: ItecParams<TParams>,
    config?: ItecAxiosRequestConfig
  ): Promise<TResponse> {
    try {
      const response = await this.axiosInstance.delete<BaseResponse<TResponse>>(
        url,
        {
          ...config,
          params: {
            ...params.params,
            ...params.pagination,
          },
        }
      );

      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }
}
